import React, { useState, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
} from '../ui'
import { useCreatePosition, useUpdatePosition, type Position } from '../../hooks/usePositions'
import { useDepartments } from '../../hooks/useDepartments'
import { LoadingSpinner } from '../ui/loading-states'

interface PositionFormData {
  name: string
  department_id?: string | null
  code_1c?: string | null
}

interface PositionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  position?: Position | null
  mode: 'create' | 'edit'
}

export function PositionForm({ open, onOpenChange, position, mode }: PositionFormProps) {
  const [formData, setFormData] = useState<PositionFormData>({
    name: '',
    department_id: null,
    code_1c: null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load departments for selection
  const { data: departments } = useDepartments({
    limit: 100,
  })

  const createPosition = useCreatePosition()
  const updatePosition = useUpdatePosition()

  const isLoading = createPosition.isPending || updatePosition.isPending

  // Initialize form data when dialog opens or position changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && position) {
        setFormData({
          name: position.name || '',
          department_id: position.department_id || null,
          code_1c: position.code_1c || null,
        })
      } else {
        setFormData({
          name: '',
          department_id: null,
          code_1c: null,
        })
      }
      setErrors({})

      // Сброс ошибок при открытии формы
      createPosition.reset()
      updatePosition.reset()
    }
  }, [open, mode, position]) // Убрал createPosition и updatePosition из зависимостей

  const handleInputChange = (field: keyof PositionFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let value: string | number | null = e.target.value

    // Convert empty string to null for optional fields
    if (value === '' && (field === 'department_id' || field === 'code_1c')) {
      value = null
    }

    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название должности обязательно'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Название должно содержать минимум 2 символа'
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Prepare data for sending
      const dataToSend: any = {
        name: formData.name.trim(),
      }

      // Handle optional fields - не передаем поля если они пустые
      if (formData.department_id && formData.department_id.trim() !== '') {
        dataToSend.department_id = formData.department_id.trim()
      }
      // Если department_id пустой, вообще не передаем это поле

      if (formData.code_1c && formData.code_1c.trim() !== '') {
        dataToSend.code_1c = formData.code_1c.trim()
      }
      // Если code_1c пустой, вообще не передаем это поле


      if (mode === 'create') {
        await createPosition.mutateAsync(dataToSend)
      } else if (mode === 'edit' && position) {
        await updatePosition.mutateAsync({
          id: position.id,
          data: dataToSend
        })
      }

      // Закрываем форму только если операция прошла успешно
      onOpenChange(false)
    } catch (error) {
      console.error('Form submission error:', error)
      // Форма остается открытой чтобы показать ошибку
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать должность' : 'Редактировать должность'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Заполните информацию для создания новой должности.'
              : 'Измените информацию о должности.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Position Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Название должности *
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange('name')}
              placeholder="Введите название должности"
              disabled={isLoading}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.name}
              </div>
            )}
          </div>

          {/* Department */}
          <div>
            <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
              Подразделение
            </label>
            <Select
              id="department_id"
              value={formData.department_id || ''}
              onChange={handleInputChange('department_id')}
              disabled={isLoading}
            >
              <SelectOption value="">Не указано</SelectOption>
              {departments?.data?.map(dept => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>
          </div>

          {/* 1C Code */}
          <div>
            <label htmlFor="code_1c" className="block text-sm font-medium text-gray-700 mb-1">
              Код 1С
            </label>
            <Input
              id="code_1c"
              type="text"
              value={formData.code_1c || ''}
              onChange={handleInputChange('code_1c')}
              placeholder="Код из системы 1С"
              disabled={isLoading}
            />
          </div>

          {/* Show API errors */}
          {(createPosition.error || updatePosition.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = (createPosition.error || updatePosition.error) as any
                  const message = error?.message
                  const status = error?.status

                  if (status === 400) {
                    return 'Проверьте правильность заполнения полей'
                  } else if (status === 409) {
                    return 'Должность с таким названием уже существует'
                  } else if (status === 403) {
                    return 'У вас нет прав для выполнения этой операции'
                  } else if (status === 500) {
                    return 'Произошла ошибка на сервере. Попробуйте позже'
                  } else {
                    return message || `Произошла ошибка при ${mode === 'create' ? 'создании' : 'обновлении'} должности`
                  }
                })()}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}